import { useState, useEffect, useCallback } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Trash2, BadgeCheck, MapPin, Building2, Paperclip } from "lucide-react";
import { PROVINCIES, fileUrl } from "@/lib/jobOptions";
import {
  ELEMENT_TYPES, KLEUREN, MATERIALEN, VERDELING, ONDERVERDELING,
  GLASKEUZE, BEGLAZING, VENTILATIE, ROLLUIK, emptyElement,
} from "@/lib/offerteOptions";
import { MeetHandleiding } from "@/components/offerte/MeetHandleiding";
import { isFreePeriod } from "@/lib/pricing";
import { toast } from "sonner";

const Sel = ({ label, value, onChange, options, testid }) => (
  <div>
    <Label className="text-xs font-bold text-slate-700">{label}</Label>
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger data-testid={testid} className="rounded-sm border-slate-300"><SelectValue /></SelectTrigger>
      <SelectContent>
        {options.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
      </SelectContent>
    </Select>
  </div>
);

export function OfferteForm({ user, onSubmit, submitting, submitLabel, uploadPath = "/jobs/upload", bedrijvenPath = "/bedrijven" }) {
  const free = isFreePeriod();
  const effectiveSubmitLabel = submitLabel || (free ? "Gratis · offerte aanvragen" : "Offerte aanvragen · €3 betalen");
  const [elementen, setElementen] = useState([emptyElement()]);
  const [contact, setContact] = useState({
    naam: user?.name || "", telefoon: user?.telefoon || "", email: user?.email || "",
    adres_straat: "", adres_postcode: "", adres_gemeente: "", type_klant: "particulier",
    werf_straat: "", werf_postcode: "", werf_gemeente: "",
  });
  const [bedrijven, setBedrijven] = useState([]);
  const [provFilter, setProvFilter] = useState("alle");
  const [selectedBedrijven, setSelectedBedrijven] = useState([]);
  const [bijlagen, setBijlagen] = useState([]);
  const [uploading, setUploading] = useState(false);

  const loadBedrijven = useCallback(async () => {
    try {
      const params = {};
      if (provFilter !== "alle") params.provincie = provFilter;
      const { data } = await api.get(bedrijvenPath, { params });
      setBedrijven(data.bedrijven);
    } catch (e) { toast.error("Bedrijven laden mislukt"); }
  }, [provFilter, bedrijvenPath]);

  useEffect(() => { loadBedrijven(); }, [loadBedrijven]);

  const setEl = (i, patch) => setElementen((arr) => arr.map((e, idx) => (idx === i ? { ...e, ...patch } : e)));
  const addEl = () => setElementen((arr) => [...arr, emptyElement()]);
  const removeEl = (i) => setElementen((arr) => (arr.length > 1 ? arr.filter((_, idx) => idx !== i) : arr));

  const toggleBedrijf = (id) => {
    setSelectedBedrijven((s) => {
      if (s.includes(id)) return s.filter((x) => x !== id);
      if (s.length >= 4) { toast.error("Je kunt maximaal 4 bedrijven aanduiden"); return s; }
      return [...s, id];
    });
  };

  const handleSubmit = () => {
    if (selectedBedrijven.length === 0) { toast.error("Kies minstens één bedrijf"); return; }
    if (!contact.naam || !contact.telefoon || !contact.email || !contact.adres_straat || !contact.adres_postcode || !contact.adres_gemeente) {
      toast.error("Vul alle verplichte contactgegevens in");
      return;
    }
    onSubmit({ ...contact, elementen: elementen.map(({ _id, ...rest }) => rest), bedrijf_ids: selectedBedrijven, bijlagen });
  };

  const uploadFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    const allowed = ["application/pdf", "image/png", "image/gif", "image/jpeg"];
    if (!allowed.includes(file.type)) { toast.error("Alleen PDF, PNG, JPG of GIF toegestaan"); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(uploadPath, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setBijlagen((b) => [...b, data]);
      toast.success(`${data.naam} toegevoegd`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Upload mislukt");
    } finally { setUploading(false); }
  };

  const removeBijlage = (id) => setBijlagen((b) => b.filter((x) => x.id !== id));

  return (
    <div className="max-w-3xl space-y-6">
      <div className="bg-orange-50 border border-orange-200 rounded-md px-4 py-3 text-sm text-orange-800 font-semibold">
        Vraag een offerte aan voor ramen, deuren, Veluxramen en garagepoorten. Kies tot 4 bedrijven — zij ontvangen je aanvraag per e-mail.
      </div>

      <div data-testid="offerte-fee-notice" className="bg-slate-950 text-white rounded-md px-5 py-4">
        {free ? (
          <>
            <p className="font-display font-bold text-green-400 mb-1">Gratis tot 31 december 2026</p>
            <p className="text-sm text-slate-200 leading-relaxed">
              Je prijsaanvraag is volledig <strong>gratis</strong> — geen kosten, geen abonnement.
              De gekozen bedrijven worden meteen verwittigd en nemen contact met je op.
            </p>
          </>
        ) : (
          <>
            <p className="font-display font-bold text-orange-400 mb-1">Een prijsaanvraag kost €3</p>
            <p className="text-sm text-slate-200 leading-relaxed">
              Deze kleine bijdrage voorkomt misbruik en valse aanvragen en zorgt voor ernstige prijsaanvragen.
              Je krijgt de <strong>€3 volledig terug</strong> bij een eventuele bestelling bij één van de aangevraagde bedrijven.
            </p>
          </>
        )}
      </div>

      {/* Elementen */}
      {elementen.map((el, i) => (
        <div key={el._id} data-testid="offerte-element" className="bg-white border border-slate-200 rounded-md p-5 space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="font-display font-bold text-slate-950">Element {i + 1}</h3>
            {elementen.length > 1 && (
              <button type="button" data-testid="remove-element-btn" onClick={() => removeEl(i)} className="text-slate-400 hover:text-red-600">
                <Trash2 className="w-4 h-4" />
              </button>
            )}
          </div>
          <div className="grid sm:grid-cols-3 gap-3">
            <Sel label="Type" value={el.type} onChange={(v) => setEl(i, { type: v })}
              options={ELEMENT_TYPES.map((t) => t.label)} testid={`element-type-${i}`} />
            <div>
              <Label className="text-xs font-bold text-slate-700">Breedte (cm)</Label>
              <Input data-testid={`element-breedte-${i}`} value={el.breedte} onChange={(e) => setEl(i, { breedte: e.target.value })} className="rounded-sm border-slate-300" />
            </div>
            <div>
              <Label className="text-xs font-bold text-slate-700">Hoogte (cm)</Label>
              <Input data-testid={`element-hoogte-${i}`} value={el.hoogte} onChange={(e) => setEl(i, { hoogte: e.target.value })} className="rounded-sm border-slate-300" />
            </div>
            <div>
              <Label className="text-xs font-bold text-slate-700">Aantal</Label>
              <Input type="number" min={1} data-testid={`element-aantal-${i}`} value={el.aantal} onChange={(e) => setEl(i, { aantal: Number(e.target.value) })} className="rounded-sm border-slate-300" />
            </div>
            <Sel label="Kleur binnenzijde" value={el.kleur_binnen} onChange={(v) => setEl(i, { kleur_binnen: v })} options={KLEUREN} />
            <Sel label="Kleur buitenzijde" value={el.kleur_buiten} onChange={(v) => setEl(i, { kleur_buiten: v })} options={KLEUREN} />
            <Sel label="Materiaal" value={el.materiaal} onChange={(v) => setEl(i, { materiaal: v })} options={MATERIALEN} />
            <Sel label="Verdeling" value={el.verdeling} onChange={(v) => setEl(i, { verdeling: v })} options={VERDELING} />
            <Sel label="Onderverdeling" value={el.onderverdeling} onChange={(v) => setEl(i, { onderverdeling: v })} options={ONDERVERDELING} />
            <Sel label="Glaskeuze" value={el.glaskeuze} onChange={(v) => setEl(i, { glaskeuze: v })} options={GLASKEUZE} />
            <Sel label="Beglazing" value={el.beglazing} onChange={(v) => setEl(i, { beglazing: v })} options={BEGLAZING} />
            <Sel label="Ventilatie" value={el.ventilatie} onChange={(v) => setEl(i, { ventilatie: v })} options={VENTILATIE} />
            <Sel label="Rolluik" value={el.rolluik} onChange={(v) => setEl(i, { rolluik: v })} options={ROLLUIK} />
          </div>
          <div>
            <Label className="text-xs font-bold text-slate-700">Opmerkingen</Label>
            <Textarea data-testid={`element-opmerkingen-${i}`} value={el.opmerkingen} onChange={(e) => setEl(i, { opmerkingen: e.target.value })} className="rounded-sm border-slate-300" rows={2} />
          </div>
        </div>
      ))}
      <Button type="button" data-testid="add-element-btn" onClick={addEl} variant="outline"
        className="rounded-sm font-semibold border-slate-300 hover:border-orange-400 hover:text-orange-600">
        <Plus className="w-4 h-4 mr-2" /> Nog een element toevoegen
      </Button>

      {/* Bijlagen + meethandleiding */}
      <div className="bg-white border border-slate-200 rounded-md p-5 space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h3 className="font-display font-bold text-slate-950">Foto's & plannen <span className="text-sm font-normal text-slate-400">(optioneel — PDF, JPG, PNG of GIF)</span></h3>
          <MeetHandleiding />
        </div>
        <p className="text-sm text-slate-500">Voeg foto's van de huidige situatie of een plan toe. Zo kunnen bedrijven een nauwkeurigere offerte maken.</p>
        <div className="space-y-2">
          {bijlagen.map((b) => (
            <div key={b.id} data-testid="offerte-bijlage-item" className="flex items-center justify-between bg-slate-50 border border-slate-200 rounded-sm px-3 py-2">
              <a href={fileUrl(b.id)} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-sm text-slate-700 hover:text-orange-600 truncate">
                <Paperclip className="w-3.5 h-3.5 flex-shrink-0" /> <span className="truncate">{b.naam}</span>
              </a>
              <button type="button" data-testid="remove-offerte-bijlage-btn" onClick={() => removeBijlage(b.id)} className="text-slate-400 hover:text-red-600 flex-shrink-0 ml-2">
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
        <label className="inline-flex items-center gap-2 cursor-pointer border border-dashed border-slate-300 rounded-sm px-4 py-2 text-sm font-semibold text-slate-600 hover:border-orange-400 hover:text-orange-600">
          <Paperclip className="w-4 h-4" />
          {uploading ? "Bezig met uploaden..." : bijlagen.length ? "Nog een bestand toevoegen" : "Bestand toevoegen"}
          <input data-testid="offerte-bijlage-input" type="file" accept=".pdf,.png,.gif,.jpg,.jpeg,application/pdf,image/png,image/gif,image/jpeg" onChange={uploadFile} disabled={uploading} className="hidden" />
        </label>
      </div>

      {/* Contact */}
      <div className="bg-white border border-slate-200 rounded-md p-5 space-y-4">
        <h3 className="font-display font-bold text-slate-950">Uw contactgegevens</h3>
        <div className="grid sm:grid-cols-2 gap-3">
          <div>
            <Label className="text-xs font-bold text-slate-700">Naam *</Label>
            <Input data-testid="offerte-naam" value={contact.naam} onChange={(e) => setContact({ ...contact, naam: e.target.value })} className="rounded-sm border-slate-300" />
          </div>
          <div>
            <Label className="text-xs font-bold text-slate-700">Telefoonnummer *</Label>
            <Input data-testid="offerte-telefoon" value={contact.telefoon} onChange={(e) => setContact({ ...contact, telefoon: e.target.value })} className="rounded-sm border-slate-300" />
          </div>
          <div className="sm:col-span-2">
            <Label className="text-xs font-bold text-slate-700">E-mailadres *</Label>
            <Input data-testid="offerte-email" type="email" value={contact.email} onChange={(e) => setContact({ ...contact, email: e.target.value })} className="rounded-sm border-slate-300" />
          </div>
          <div className="sm:col-span-2">
            <Label className="text-xs font-bold text-slate-700">Straat en nummer *</Label>
            <Input data-testid="offerte-straat" value={contact.adres_straat} onChange={(e) => setContact({ ...contact, adres_straat: e.target.value })} className="rounded-sm border-slate-300" />
          </div>
          <div>
            <Label className="text-xs font-bold text-slate-700">Postcode *</Label>
            <Input data-testid="offerte-postcode" value={contact.adres_postcode} onChange={(e) => setContact({ ...contact, adres_postcode: e.target.value })} className="rounded-sm border-slate-300" />
          </div>
          <div>
            <Label className="text-xs font-bold text-slate-700">Gemeente *</Label>
            <Input data-testid="offerte-gemeente" value={contact.adres_gemeente} onChange={(e) => setContact({ ...contact, adres_gemeente: e.target.value })} className="rounded-sm border-slate-300" />
          </div>
          <Sel label="Type klant *" value={contact.type_klant === "particulier" ? "Particulier" : "Professional"}
            onChange={(v) => setContact({ ...contact, type_klant: v === "Particulier" ? "particulier" : "professional" })}
            options={["Particulier", "Professional"]} testid="offerte-type-klant" />
        </div>
      </div>

      {/* Bedrijfselectie */}
      <div className="bg-white border border-slate-200 rounded-md p-5 space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h3 className="font-display font-bold text-slate-950">Kies bedrijven <span className="text-sm font-normal text-slate-400">(max 4 — {selectedBedrijven.length} gekozen)</span></h3>
          <div className="w-56">
            <Select value={provFilter} onValueChange={setProvFilter}>
              <SelectTrigger data-testid="offerte-prov-filter" className="rounded-sm border-slate-300"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="alle">Alle provincies</SelectItem>
                {PROVINCIES.map((g) => (
                  <div key={g.groep}>
                    <div className="px-2 py-1.5 text-xs font-bold text-slate-400 uppercase tracking-wide">{g.groep}</div>
                    {g.opties.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                  </div>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        {bedrijven.length === 0 && <p className="text-sm text-slate-400 py-4">Geen bedrijven gevonden voor deze filter.</p>}
        <div className="grid sm:grid-cols-2 gap-3">
          {bedrijven.map((b) => {
            const sel = selectedBedrijven.includes(b.id);
            return (
              <button key={b.id} type="button" data-testid="offerte-bedrijf-option" onClick={() => toggleBedrijf(b.id)}
                className={`text-left border rounded-md p-3 transition-colors ${sel ? "border-orange-500 bg-orange-50" : "border-slate-200 hover:border-slate-400"}`}>
                <div className="flex items-center gap-2">
                  <Building2 className="w-4 h-4 text-slate-400" />
                  <span className="font-display font-bold text-slate-950">{b.name}</span>
                  {b.btw_geverifieerd && <BadgeCheck className="w-4 h-4 text-green-600" title="Geverifieerd" />}
                </div>
                <p className="text-xs text-slate-500 flex items-center gap-1 mt-1"><MapPin className="w-3 h-3" /> {b.provincie || b.regio || "—"}</p>
                {(() => {
                  const aanbod = [...(b.aanbod_voor || []).filter((x) => x !== "Andere")];
                  if ((b.aanbod_voor || []).includes("Andere") && b.aanbod_andere) aanbod.push(b.aanbod_andere);
                  const merken = [
                    ...(b.merken_pvc || []), b.merken_pvc_andere,
                    ...(b.merken_alu || []), b.merken_alu_andere,
                    ...(b.merken_hout || []), b.merken_hout_andere,
                  ].filter(Boolean);
                  return (
                    <div className="mt-2 space-y-1.5">
                      {aanbod.length > 0 && (
                        <div className="flex flex-wrap gap-1">
                          {aanbod.map((a) => (
                            <span key={a} className="text-[10px] font-semibold bg-slate-100 text-slate-600 rounded-sm px-1.5 py-0.5">{a}</span>
                          ))}
                        </div>
                      )}
                      {(b.materialen || []).length > 0 && (
                        <p className="text-[11px] text-slate-500"><span className="font-semibold text-slate-600">Materialen:</span> {(b.materialen || []).join(", ")}</p>
                      )}
                      {merken.length > 0 && (
                        <p className="text-[11px] text-slate-400 truncate"><span className="font-semibold text-slate-500">Merken:</span> {merken.join(", ")}</p>
                      )}
                      {(b.portfolio || []).length > 0 && (
                        <div className="flex gap-1 pt-1">
                          {(b.portfolio || []).slice(0, 4).map((p) => (
                            <img key={p.file_id} src={fileUrl(p.file_id)} alt={p.titel || "Portfolio"}
                              className="w-11 h-11 object-cover rounded-sm border border-slate-200" loading="lazy" />
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })()}
              </button>
            );
          })}
        </div>
      </div>

      <Button data-testid="submit-offerte-btn" onClick={handleSubmit} disabled={submitting}
        className="bg-orange-500 hover:bg-orange-600 text-white rounded-sm font-bold h-12 w-full">
        {submitting ? "Bezig..." : effectiveSubmitLabel}
      </Button>
    </div>
  );
}
